import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/useToast'
import { databaseKeys } from '@/hooks/useDatabase'
import { AgentProxyService } from '@/services/agentProxyService'

interface DropOperationData {
  type: 'database' | 'schema' | 'table' | 'view' | 'function' | 'procedure' | 'trigger' | 'sequence' | 'extension' | 'index' | 'user'
  name: string
  database: string
  schema?: string
  connectionId: string
}

interface DropHookOptions {
  onSuccess?: (data: DropOperationData) => void
}

export function useDropOperation(options?: DropHookOptions) {
  const [isDropDialogOpen, setIsDropDialogOpen] = useState(false)
  const [dropData, setDropData] = useState<DropOperationData | null>(null)
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  const dropMutation = useMutation({
    mutationFn: async ({ data, cascade }: { data: DropOperationData; cascade?: boolean }) => {
      const { type, name, database, schema, connectionId } = data

      switch (type) {
        case 'database':
          await AgentProxyService.dropDatabase(connectionId, name)
          break

        case 'schema':
          if (!schema) throw new Error('Schema name is required')
          await AgentProxyService.dropSchema(connectionId, database, name, cascade)
          break

        case 'table':
          await AgentProxyService.dropTable(connectionId, database, name, { 
            schemaName: schema, 
            cascade 
          })
          break

        case 'view':
          await AgentProxyService.dropView(connectionId, database, name, schema, cascade)
          break

        case 'function':
          await AgentProxyService.dropFunction(connectionId, database, name, schema)
          break

        case 'procedure':
          await AgentProxyService.dropProcedure(connectionId, database, name, schema)
          break

        case 'trigger':
          // Trigger operations not yet implemented in agent proxy
          throw new Error('Trigger drop operation not yet implemented')

        case 'sequence':
          // Sequence operations not yet implemented in agent proxy
          throw new Error('Sequence drop operation not yet implemented')

        case 'extension':
          // Extension operations not yet implemented in agent proxy
          throw new Error('Extension drop operation not yet implemented')

        case 'index':
          await AgentProxyService.dropIndex(connectionId, database, name, schema)
          break

        case 'user':
          await AgentProxyService.dropUser(connectionId, database, name)
          break

        default:
          throw new Error(`Unsupported drop operation: ${type as string}`)
      }
    },
    onSuccess: (_, { data }) => {
      const { type, name, database, schema, connectionId } = data
      
      // Call the external onSuccess callback if provided
      if (options?.onSuccess) {
        options.onSuccess(data)
      }
      
      // Invalidate relevant queries to refresh the UI
      const invalidatePromises = []
      
      // Always invalidate databases query for connection
      invalidatePromises.push(
        queryClient.invalidateQueries({ queryKey: databaseKeys.list(connectionId) })
      )

      switch (type) {
        case 'database':
          // When database is dropped, immediately remove all related queries from cache to prevent further fetches
          queryClient.removeQueries({ queryKey: databaseKeys.schemas(connectionId, database) })
          queryClient.removeQueries({ queryKey: databaseKeys.tables(connectionId, database) })
          queryClient.removeQueries({ queryKey: [...databaseKeys.all, 'views', connectionId, database] })
          queryClient.removeQueries({ queryKey: [...databaseKeys.all, 'functions', connectionId, database] })
          queryClient.removeQueries({ queryKey: [...databaseKeys.all, 'procedures', connectionId, database] })
          queryClient.removeQueries({ queryKey: [...databaseKeys.all, 'triggers', connectionId, database] })
          queryClient.removeQueries({ queryKey: [...databaseKeys.all, 'sequences', connectionId, database] })
          queryClient.removeQueries({ queryKey: [...databaseKeys.all, 'indexes', connectionId, database] })
          queryClient.removeQueries({ queryKey: [...databaseKeys.all, 'extensions', connectionId, database] })
          queryClient.removeQueries({ queryKey: [...databaseKeys.all, 'events', connectionId, database] })
          queryClient.removeQueries({ queryKey: [...databaseKeys.all, 'users', connectionId, database] })
          // Also remove database details query to prevent any further attempts to fetch it
          queryClient.removeQueries({ queryKey: [...databaseKeys.all, 'details', connectionId, database] })
          
          // Cancel any ongoing queries for the dropped database to prevent errors
          queryClient.cancelQueries({ queryKey: [...databaseKeys.all, 'details', connectionId, database] })
          queryClient.cancelQueries({ queryKey: databaseKeys.schemas(connectionId, database) })
          queryClient.cancelQueries({ queryKey: databaseKeys.tables(connectionId, database) })
          queryClient.cancelQueries({ queryKey: [...databaseKeys.all, 'views', connectionId, database] })
          queryClient.cancelQueries({ queryKey: [...databaseKeys.all, 'functions', connectionId, database] })
          queryClient.cancelQueries({ queryKey: [...databaseKeys.all, 'procedures', connectionId, database] })
          queryClient.cancelQueries({ queryKey: [...databaseKeys.all, 'triggers', connectionId, database] })
          queryClient.cancelQueries({ queryKey: [...databaseKeys.all, 'sequences', connectionId, database] })
          queryClient.cancelQueries({ queryKey: [...databaseKeys.all, 'indexes', connectionId, database] })
          queryClient.cancelQueries({ queryKey: [...databaseKeys.all, 'extensions', connectionId, database] })
          queryClient.cancelQueries({ queryKey: [...databaseKeys.all, 'events', connectionId, database] })
          queryClient.cancelQueries({ queryKey: [...databaseKeys.all, 'users', connectionId, database] })
          
          // Invalidate broader database list
          invalidatePromises.push(
            queryClient.invalidateQueries({ queryKey: ['databases'] })
          )
          break

        case 'schema':
          // When schema is dropped, invalidate schema and all its content queries
          invalidatePromises.push(
            queryClient.invalidateQueries({ queryKey: databaseKeys.schemas(connectionId, database) }),
            queryClient.invalidateQueries({ queryKey: databaseKeys.tables(connectionId, database, schema) }),
            queryClient.invalidateQueries({ queryKey: [...databaseKeys.all, 'views', connectionId, database, schema] }),
            queryClient.invalidateQueries({ queryKey: [...databaseKeys.all, 'functions', connectionId, database, schema] }),
            queryClient.invalidateQueries({ queryKey: [...databaseKeys.all, 'procedures', connectionId, database, schema] }),
            queryClient.invalidateQueries({ queryKey: [...databaseKeys.all, 'triggers', connectionId, database, schema] }),
            queryClient.invalidateQueries({ queryKey: [...databaseKeys.all, 'sequences', connectionId, database, schema] }),
            queryClient.invalidateQueries({ queryKey: [...databaseKeys.all, 'indexes', connectionId, database, schema] })
          )
          break

        case 'table':
          // When table is dropped, invalidate table list and related queries
          invalidatePromises.push(
            queryClient.invalidateQueries({ queryKey: databaseKeys.tables(connectionId, database, schema) }),
            queryClient.invalidateQueries({ queryKey: databaseKeys.columns(connectionId, database, name, schema) }),
            queryClient.invalidateQueries({ queryKey: databaseKeys.constraints(connectionId, database, name, schema) }),
            queryClient.invalidateQueries({ queryKey: [...databaseKeys.all, 'triggers', connectionId, database, schema] }),
            queryClient.invalidateQueries({ queryKey: [...databaseKeys.all, 'indexes', connectionId, database, schema] })
          )
          break

        case 'view':
          invalidatePromises.push(
            queryClient.invalidateQueries({ queryKey: [...databaseKeys.all, 'views', connectionId, database, schema] })
          )
          break

        case 'function':
          invalidatePromises.push(
            queryClient.invalidateQueries({ queryKey: [...databaseKeys.all, 'functions', connectionId, database, schema] }),
            queryClient.invalidateQueries({ queryKey: [...databaseKeys.all, 'function-details', connectionId, database, name, schema] })
          )
          break

        case 'procedure':
          invalidatePromises.push(
            queryClient.invalidateQueries({ queryKey: [...databaseKeys.all, 'procedures', connectionId, database, schema] }),
            queryClient.invalidateQueries({ queryKey: [...databaseKeys.all, 'procedure-details', connectionId, database, name, schema] })
          )
          break

        case 'trigger':
          invalidatePromises.push(
            queryClient.invalidateQueries({ queryKey: [...databaseKeys.all, 'triggers', connectionId, database, schema] })
          )
          break

        case 'sequence':
          invalidatePromises.push(
            queryClient.invalidateQueries({ queryKey: [...databaseKeys.all, 'sequences', connectionId, database, schema] })
          )
          break

        case 'index':
          invalidatePromises.push(
            queryClient.invalidateQueries({ queryKey: [...databaseKeys.all, 'indexes', connectionId, database, schema] })
          )
          break

        case 'user':
          invalidatePromises.push(
            queryClient.invalidateQueries({ queryKey: [...databaseKeys.all, 'users', connectionId, database] })
          )
          break
          
        case 'extension':
          invalidatePromises.push(
            queryClient.invalidateQueries({ queryKey: [...databaseKeys.all, 'extensions', connectionId, database] })
          )
          break
      }

      // Execute all invalidations
      Promise.all(invalidatePromises).catch(console.error)

      showToast('success', `${type.charAt(0).toUpperCase() + type.slice(1)} "${name}" dropped successfully`)
      setIsDropDialogOpen(false)
      setDropData(null)
    },
    onError: (error: unknown) => {
      console.error('Drop operation failed:', error)
      const errorObj = (error && typeof error === 'object') ? error as { message?: string; response?: { data?: { message?: string } } } : {}
      const errorMessage = errorObj.message || errorObj.response?.data?.message || `Failed to drop ${dropData?.type || 'item'}`
      showToast('error', errorMessage)
    }
  })

  const initiateDropOperation = (data: DropOperationData) => {
    setDropData(data)
    setIsDropDialogOpen(true)
  }

  const confirmDrop = (cascade?: boolean) => {
    if (dropData) {
      dropMutation.mutate({ data: dropData, cascade })
    }
  }

  const cancelDrop = () => {
    setIsDropDialogOpen(false)
    setDropData(null)
  }

  return {
    isDropDialogOpen,
    dropData,
    isDropLoading: dropMutation.isPending,
    initiateDropOperation,
    confirmDrop,
    cancelDrop
  }
}
